export const YANDEX_SUGGEST_SDK_URL =
  'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js';
export const YANDEX_SUGGEST_TOKEN_SDK_URL =
  'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-token-with-polyfills-latest.js';
export const YANDEX_LOCAL_PROVIDER_ID = 'yandex-local' as const;
export const YANDEX_LOCAL_STATE_STORAGE_KEY = 's-shot:yandex-local-state';

export type YandexLocalIntent = 'login' | 'link';

export type YandexLocalState = {
  nonce: string;
  origin: string;
  intent: YandexLocalIntent;
  callbackUrl: string | null;
};

declare global {
  interface Window {
    YaAuthSuggest?: {
      init: (
        oauthQueryParams: Record<string, string>,
        tokenPageOrigin: string,
      ) => Promise<{ handler: () => Promise<unknown> }>;
    };
    YaSendSuggestToken?: (origin: string, extraData?: Record<string, unknown>) => void;
  }
}

export function normalizeUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.pathname === '/' && !parsed.search && !parsed.hash) {
      return parsed.origin;
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

export function resolveOrigin(url: string | null | undefined): string | null {
  const normalized = normalizeUrl(url);
  if (!normalized) {
    return null;
  }

  return new URL(normalized).origin;
}

export function isLoopbackOrigin(origin: string | null | undefined): boolean {
  if (!origin) {
    return false;
  }

  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin);
}

export function getConfiguredYandexHelperUrl(): string | null {
  return normalizeUrl(process.env.NEXT_PUBLIC_YANDEX_HELPER_URL ?? null);
}

export function getConfiguredYandexHelperOrigin(helperUrl?: string | null): string | null {
  return resolveOrigin(helperUrl ?? getConfiguredYandexHelperUrl());
}

export function isYandexLocalHelperConfigured(helperUrl?: string | null): boolean {
  return Boolean(getConfiguredYandexHelperOrigin(helperUrl));
}

export function getYandexLocalConfigError(helperUrl?: string | null): string {
  if (!isYandexLocalHelperConfigured(helperUrl)) {
    return 'Локальный вход через Yandex не настроен: отсутствует NEXT_PUBLIC_YANDEX_HELPER_URL.';
  }

  return 'Вход через Yandex сейчас недоступен.';
}

function encodeBase64Url(value: string): string {
  if (typeof window === 'undefined') {
    return Buffer.from(value, 'utf8').toString('base64url');
  }

  const utf8 = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)),
  );

  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decodeBase64Url(value: string): string | null {
  try {
    if (typeof window === 'undefined') {
      return Buffer.from(value, 'base64url').toString('utf8');
    }

    const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
    const binary = atob(padded);
    const encoded = Array.from(binary)
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('');

    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

function generateNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createYandexLocalState(input: {
  origin: string;
  intent: YandexLocalIntent;
  callbackUrl?: string | null;
}): string {
  const origin = resolveOrigin(input.origin);
  if (!origin || !isLoopbackOrigin(origin)) {
    throw new Error('Локальный вход через Yandex доступен только с localhost.');
  }

  const payload: YandexLocalState = {
    nonce: generateNonce(),
    origin,
    intent: input.intent,
    callbackUrl: input.callbackUrl ?? null,
  };

  return encodeBase64Url(JSON.stringify(payload));
}

export function parseYandexLocalState(value: string | null | undefined): YandexLocalState | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const decoded = decodeBase64Url(value.trim());
  if (!decoded) {
    return null;
  }

  try {
    const parsed = JSON.parse(decoded) as Partial<YandexLocalState>;
    if (
      typeof parsed.nonce !== 'string' ||
      !parsed.nonce ||
      typeof parsed.origin !== 'string' ||
      !isLoopbackOrigin(parsed.origin) ||
      (parsed.intent !== 'login' && parsed.intent !== 'link')
    ) {
      return null;
    }

    return {
      nonce: parsed.nonce,
      origin: parsed.origin,
      intent: parsed.intent,
      callbackUrl: typeof parsed.callbackUrl === 'string' && parsed.callbackUrl ? parsed.callbackUrl : null,
    };
  } catch {
    return null;
  }
}

function findStringRecursively(value: unknown, keyNames: string[]): string | null {
  if (!value) {
    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = findStringRecursively(item, keyNames);
      if (nested) {
        return nested;
      }
    }

    return null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  for (const [key, nestedValue] of Object.entries(value)) {
    if (keyNames.includes(key) && typeof nestedValue === 'string' && nestedValue.trim()) {
      return nestedValue.trim();
    }

    const nested = findStringRecursively(nestedValue, keyNames);
    if (nested) {
      return nested;
    }
  }

  return null;
}

export function extractYandexAccessToken(value: unknown): string | null {
  return findStringRecursively(value, ['access_token', 'accessToken']);
}

export function extractYandexReturnedState(value: unknown): string | null {
  return findStringRecursively(value, ['state']);
}

async function loadExternalScript(
  src: string,
  elementId: string,
  globalName: 'YaAuthSuggest' | 'YaSendSuggestToken',
): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('Yandex helper SDK can only be loaded in the browser.');
  }

  if (window[globalName]) {
    return;
  }

  const existing = document.getElementById(elementId) as HTMLScriptElement | null;
  if (existing) {
    await new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Не удалось загрузить ${src}`)), { once: true });
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = elementId;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Не удалось загрузить ${src}`));
    document.head.appendChild(script);
  });
}

export async function loadYandexSuggestSdk(): Promise<void> {
  await loadExternalScript(YANDEX_SUGGEST_SDK_URL, 'yandex-suggest-sdk', 'YaAuthSuggest');
}

export async function loadYandexSuggestTokenSdk(): Promise<void> {
  await loadExternalScript(
    YANDEX_SUGGEST_TOKEN_SDK_URL,
    'yandex-suggest-token-sdk',
    'YaSendSuggestToken',
  );
}

export function rememberYandexLocalState(state: string) {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(YANDEX_LOCAL_STATE_STORAGE_KEY, state);
  }
}

export function takeRememberedYandexLocalState(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const state = window.sessionStorage.getItem(YANDEX_LOCAL_STATE_STORAGE_KEY);
  window.sessionStorage.removeItem(YANDEX_LOCAL_STATE_STORAGE_KEY);
  return state;
}

export async function requestLocalYandexAccessToken(input: {
  clientId: string;
  helperUrl: string;
  origin: string;
  intent: YandexLocalIntent;
  callbackUrl?: string | null;
}): Promise<{ accessToken: string }> {
  const helperUrl = normalizeUrl(input.helperUrl);
  const helperOrigin = getConfiguredYandexHelperOrigin(helperUrl);
  const origin = resolveOrigin(input.origin);
  const clientId = input.clientId.trim();

  if (!clientId) {
    throw new Error('Вход через Yandex сейчас недоступен.');
  }

  if (!origin || !isLoopbackOrigin(origin)) {
    throw new Error('Локальный вход через Yandex доступен только с localhost.');
  }

  if (!helperUrl || !helperOrigin) {
    throw new Error(getYandexLocalConfigError(helperUrl));
  }

  await loadYandexSuggestSdk();
  if (!window.YaAuthSuggest) {
    throw new Error('Не удалось инициализировать Yandex Auth SDK.');
  }

  const state = createYandexLocalState({
    origin,
    intent: input.intent,
    callbackUrl: input.callbackUrl ?? null,
  });
  rememberYandexLocalState(state);

  const { handler } = await window.YaAuthSuggest.init(
    {
      client_id: clientId,
      response_type: 'token',
      redirect_uri: helperUrl,
      scope: 'login:info login:email login:avatar',
      state,
    },
    helperOrigin,
  );

  const result = await handler();
  const accessToken = extractYandexAccessToken(result);
  const returnedState = extractYandexReturnedState(result);
  const pendingState = takeRememberedYandexLocalState();

  if (!accessToken) {
    throw new Error('Yandex helper не вернул access token.');
  }

  if (!pendingState || returnedState !== pendingState) {
    throw new Error('Yandex helper вернул некорректный state.');
  }

  return { accessToken };
}

export function parseAllowedOrigins(value: string | null | undefined): string[] {
  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(',')
    .map((item) => resolveOrigin(item))
    .filter((origin): origin is string => Boolean(origin));
}

export function isAllowedYandexLocalOrigin(origin: string | null | undefined, allowedOrigins: string[]): boolean {
  if (!origin) {
    return false;
  }

  return allowedOrigins.includes(origin);
}
